<template>
  <div
    class="package-table"
    :style="{ marginTop: presetKitId ? '25px' : '5px' }"
  >
    <div
      class="tw-flex tw-items-center tw-gap-4 tw-mt-4 tw-mb-4 tw-justify-between"
      v-if="mode === 'entrances' || mode === 'floors' || mode === 'materials'"
    >
      <div
        class="tw-flex tw-items-center tw-gap-4"
        v-if="mode === 'entrances' || mode === 'floors' || mode === 'materials'"
      >
        <CustomSelect
          v-if="mode === 'materials'"
          :options="entrancesForMaterials"
          label="Подъезд"
          option-label-key="entrance_name"
          option-value-key="project_entrance_id"
          @change="onMaterialEntranceChange"
          :value="materialEntranceId"
          placeholder="Выберите подъезд"
          clearable
        />
        <CustomSelect
          v-else
          :options="entrances"
          label="Подъезд"
          option-label-key="label"
          option-value-key="entrance"
          @change="onEntranceChange"
          :value="entrance"
          placeholder="Выберите подъезд"
          :clearable="false"
        />
        <CustomSelect
          v-if="mode === 'materials'"
          :options="floorsForMaterials"
          label="Этаж"
          option-label-key="floor_name"
          option-value-key="floor_map_id"
          @change="onMaterialFloorChange"
          :value="materialFloorMapId"
          placeholder="Выберите этаж"
          clearable
        />
        <CustomSelect
          v-else-if="mode === 'floors'"
          :options="floors"
          label="Этаж"
          option-label-key="label"
          option-value-key="level_num"
          @change="onFloorChange"
          :value="floor"
          placeholder="Выберите этаж"
        />
        <CustomSelect
          v-if="mode === 'materials'"
          :options="purchaseTypes || []"
          label="Вид закупа"
          option-label-key="purchase_name"
          option-value-key="purchase_type_id"
          @change="onPurchaseTypeChange"
          :value="materialPurchaseTypeId"
          placeholder="Выберите вид закупа"
          clearable
        />
      </div>
      <div
        class="tw-flex tw-items-center tw-gap-2"
        style="font-weight: 700; font-size: 18px;"
        v-if="!!data?.length || (mode === 'materials' && !!materialsData?.length )"
      >
        <UploadExcelBtn
          v-if="(mode === 'entrances' && entrance) || (mode === 'floors' && floor && entrance) || (mode === 'materials')"
          class="tw-mr-4"
          :loading="isExportingExcel"
          :disabled="isExportingExcel"
          @click="onExportExcel"
        />
        <Amount :value="mode === 'materials' ? materialsAmount : amount" :round="2" /> тг.
      </div>
    </div>

    <q-linear-progress v-if="isFetching" indeterminate />
    
    <!-- Таблица для режима материалов -->
    <CustomTable
      v-if="mode === 'materials'"
      :rowsData="materialsData"
      :customHeader="true"
      :custom-body="true"
      :small="true"
    >
      <template #customHeader="innerProps">
        <q-tr>
          <q-th align="center" style="font-weight: 700;"> # </q-th>
          <q-th align="left" style="font-weight: 700;"> Материал </q-th>
          <q-th align="right" style="font-weight: 700;"> Количество </q-th>
          <q-th align="left" style="font-weight: 700;"> Ед. изм. </q-th>
          <q-th align="right" style="font-weight: 700;"> Цена, тг </q-th>
          <q-th align="right" style="font-weight: 700;"> Итого, тг </q-th>
        </q-tr>
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.rowIndex + 1 }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.material_name }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.material_amount }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.material_unit_name }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.material_price_origin" :round="2" />
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.material_price_origin * props.row.material_amount" :round="2" />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <!-- Таблица для режимов по подъездам/этажам -->
    <CustomTable
      v-else
      :title="tableTitle"
      :rowsData="data"
      :customHeader="true"
      :custom-body="true"
      :small="true"
    >
      <template #customHeader="innerProps">
        <q-tr>
          <q-th align="center" rowspan="2"> # </q-th>
          <q-th align="left" rowspan="2" style="font-weight: 700;">
            Конструктив
          </q-th>
          <q-th align="center" colspan="5" style="font-weight: 700;">
            СМР
          </q-th>
          <q-th align="center" colspan="7" style="font-weight: 700;">
            Материал
          </q-th>
          <q-th align="right" rowspan="2" style="font-weight: 700;">
            Итого, тг
          </q-th>
        </q-tr>
        <q-tr class="sticky innerTableHeader">
          <q-th
            align="center"
            style="border-left: 1px solid rgba(0, 0, 0, 0.12);"
          >
            Ед. изм.
          </q-th>
          <q-th align="right"> Кол-во </q-th>
          <q-th align="right"> Цена, тг </q-th>
          <q-th align="right"> Сумма, тг </q-th>
          <q-th align="right"> НДС </q-th>

          <q-th align="center"> Наименование </q-th>
          <q-th align="right"> Норма расхода материала </q-th>
          <q-th align="center"> Ед. изм. </q-th>
          <q-th align="right"> Кол-во </q-th>
          <q-th align="right"> Цена, тг </q-th>
          <q-th align="right"> Сумма, тг </q-th>
          <q-th align="right"> НДС </q-th>
        </q-tr>
      </template>
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.index }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.wname }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.wunt }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.wqty }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.wprc" :round="2" />
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <span v-if="props.row.is_incloude_nds_work_set">
              <q-tooltip>Сумма содержит НДС</q-tooltip>
              <Amount :value="props.row.wsum" :round="2" />
            </span>
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            <span
              v-if="props.row.is_incloude_nds_work_set"
              class="tw-text-primary-light tw-cursor-pointer"
              ><q-tooltip>Сумма содержит НДС</q-tooltip
              >{{ props.row.nds_percent_work_set }}%</span
            >
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.mname }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.wqty && props.row.wqty !== 0 && props.row.mqty != null ? (Number(props.row.mqty) / Number(props.row.wqty)).toFixed(4) : '-' }}
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.munt }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            {{ props.row.mqty }}
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.mprc" :round="2" />
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <span v-if="props.row.is_incloude_nds_material">
              <q-tooltip>Сумма содержит НДС</q-tooltip>
              <Amount :value="props.row.msum" :round="2" />
            </span>
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            <span
              v-if="props.row.is_incloude_nds_material"
              class="tw-text-primary-light tw-cursor-pointer"
              ><q-tooltip>Сумма содержит НДС</q-tooltip
              >{{ props.row.nds_percent_material }}%</span
            >
          </custom-table-cell>
          <custom-table-cell align="right" vertical-align="center">
            <Amount :value="props.row.ttl" :round="2" />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import Amount from '@/components/common/Amount.vue';
import UploadExcelBtn from '@/components/common/UploadExcelBtn.vue';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import CustomSelect from '@/components/form/CustomSelect';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { getProjectEntranceFloors, getProjectEntrances, getProjectEntranceSmeta, getProjectFloorSmeta, getProjectSmetaMaterialReport } from "../ProjectsEntry/services";
import { getEntrancesByProject } from '../ProjectsEntry/services';
import { getEntranceApartments } from '@/views/ResidentialSettings/services';
import { getPackagePurchaseTypes } from './services';
import { downloadFile } from '@/services';

const props = defineProps({
  presetKitId: {
    type: Number,
    required: false
  },
  title: {
    type: String,
    default: ''
  },
  projectId: {
    type: Number,
  },
  mode: {
    type: String,
  }
})
const isFetching = ref(false)
const isExportingExcel = ref(false)
const data = ref([])
const entrance = ref(null)
const floor = ref(null)

// Для режима материалов
const materialsData = ref([])
const materialEntranceId = ref(null)
const materialFloorMapId = ref(null)
const materialPurchaseTypeId = ref(null)
const entrancesForMaterials = ref([])
const floorsForMaterials = ref([])

const tableTitle = computed(() => props.title)

const amount = computed(() => data.value.reduce((acc, item) => {
  return acc + (item.ttl || 0)
}, 0))

const materialsAmount = computed(() => {
  const total = materialsData.value.reduce((acc, item) => {
    return acc + (item.material_price_origin * item.material_amount || 0)
  }, 0)
  return Math.round(total)
})

const onExportExcel = async () => {
  try {
    let url = `/project_list/project/read/material_smeta/${props.projectId}/${entrance.value}/excel`
    let params = {}
    let title = ''

    if (props.mode === 'floors') {
      url = `/project_list/project/read/material_smeta/${props.projectId}/${entrance.value}/${floor.value}/excel`
      title = `Смета по этажу: подъезд ${entrance.value || ''}, этаж ${floor.value || ''}`
    }

    if (props.mode === 'materials') {
      url = `/project_list/project/smeta_material_report/excel/`
      if (props.projectId) params.project_id = props.projectId
      if (materialEntranceId.value) params.project_entrance_id = materialEntranceId.value
      if (materialFloorMapId.value) params.floor_map_id = materialFloorMapId.value
      if (materialPurchaseTypeId.value) params.purchase_type_id = materialPurchaseTypeId.value
      title = 'Смета по материалам'
    }

    if (!title) {
      title = `Смета по подъезду: ${entrance.value || ''}`
    }

    isExportingExcel.value = true
    await downloadFile(url, title, {params}, 'xlsx', 'get', true)
  } catch (e) {
  } finally {
    isExportingExcel.value = false
  }
}

const getData = async () => {
  if (props.mode === 'materials') {
    await getMaterialsData()
    return
  }
  
  let func = getProjectEntranceSmeta
  if(props.mode === 'floors')
    func = getProjectFloorSmeta
  isFetching.value = true
  const res = await func(props.projectId, entrance.value, floor.value)
  isFetching.value = false
  data.value = res || []
}

const getMaterialsData = async () => {
  isFetching.value = true
  const res = await getProjectSmetaMaterialReport(
    props.projectId,
    materialEntranceId.value,
    materialFloorMapId.value,
    materialPurchaseTypeId.value
  )
  isFetching.value = false
  materialsData.value = res || []
}

const loadEntrancesForMaterials = async () => {
  const res = await getEntrancesByProject(props.projectId)
  if (res && res.length > 0) {
    entrancesForMaterials.value = res
  }
}

const loadFloorsForMaterials = async () => {
  if (!materialEntranceId.value) {
    floorsForMaterials.value = []
    return
  }
  
  const res = await getEntranceApartments({
    project_entrance_id: materialEntranceId.value
  })
  if (res && res.data && res.data.length > 0) {
    floorsForMaterials.value = res.data.map(floor => ({
      ...floor,
      floor_name: `Этаж ${floor.floor}`
    }))
  }
}

useQuery({
  queryKey: ['materials', props.projectId || props.presetKitId || props.mode],
  queryFn: () => getData(),
  enabled: computed(() => !!props.projectId || !!props.presetKitId || !!props.mode)
})

const {data: entrances, refetch} = useQuery({
  queryKey: ['entrances', props.mode],
  queryFn: () => getProjectEntrances(props.projectId),
  enabled: false
})

const {data: floors, refetch: refetchFloors} = useQuery({
  queryKey: ['floors', props.mode],
  queryFn: () => getProjectEntranceFloors(props.projectId, entrance.value),
  enabled: false
})

const { data: purchaseTypes } = useQuery({
  queryKey: ['purchaseTypes'],
  queryFn: getPackagePurchaseTypes,
  initialData: [],
})

function onEntranceChange(value) {
  entrance.value = value
  floor.value = null
  getData()
  if(props.mode === 'floors')
    refetchFloors()
}

function onFloorChange(value) {
  floor.value = value
  getData()
}

function onMaterialEntranceChange(value) {
  materialEntranceId.value = value
  materialFloorMapId.value = null
  floorsForMaterials.value = []
  if (value) {
    loadFloorsForMaterials()
  }
  getMaterialsData()
}

function onMaterialFloorChange(value) {
  materialFloorMapId.value = value
  getMaterialsData()
}

function onPurchaseTypeChange(value) {
  materialPurchaseTypeId.value = value
  getMaterialsData()
}

watch(
  () => props.mode,
  (newMode) => {
    getData()
    if (newMode === 'entrances' || newMode === 'floors') {
      if(!entrance.value?.length)
        refetch()
    }
    if(newMode === 'floors') {
      if(!floors.value?.length)
        refetchFloors()
    }
    if (newMode === 'materials') {
      loadEntrancesForMaterials()
      getMaterialsData()
    }
  },
  { immediate: true }
)
watch(
  () => props.projectId,
  (newMode) => {
    refetch()
    data.value = null
    floor.value = null
    entrance.value = null
    
    // Перезагрузка данных для режима материалов
    if (props.mode === 'materials') {
      materialEntranceId.value = null
      materialFloorMapId.value = null
      materialPurchaseTypeId.value = null
      entrancesForMaterials.value = []
      floorsForMaterials.value = []
      loadEntrancesForMaterials()
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.package-table {
  margin-top: 25px;
  .table-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    .row {
      margin-right: 16px;
    }
  }
  .createBtn {
    width: auto;
    font-weight: 400;
    height: 44px;
  }
}
</style>
